import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface UseAutoScrollOptions {
  enabled?: boolean;
  threshold?: number;
  smooth?: boolean;
  /**
   * If false, the hook won't automatically follow content changes.
   * Manual `scrollToBottom()` still works.
   */
  autoScrollOnContentChange?: boolean;
}

interface UseAutoScrollReturn {
  scrollRef: React.RefObject<HTMLDivElement>;
  bottomRef: (node?: Element | null) => void;
  scrollToBottom: () => void;
  isAtBottom: boolean;
}

/**
 * Custom hook for intelligent auto-scrolling in chat/transcript interfaces
 *
 * Auto-scrolls to bottom when:
 * - User is already at the bottom (viewing latest messages)
 * - New content is added
 *
 * Preserves scroll position when:
 * - User has scrolled up to read history
 */
export const useAutoScroll = (
  options: UseAutoScrollOptions = {}
): UseAutoScrollReturn => {
  const { enabled = true, threshold = 50, smooth = true, autoScrollOnContentChange = true } = options;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const lastScrollHeight = useRef(0);

  // Use IntersectionObserver to track if bottom anchor is visible
  const { ref: bottomRef, inView: isAtBottom } = useInView({
    threshold: 0,
    rootMargin: `0px 0px ${threshold}px 0px`,
  });

  // Update auto-scroll behavior based on bottom visibility
  useEffect(() => {
    // Strictly gate auto-scroll by bottom visibility.
    // If the user is not at the bottom, we should not fight their scroll position.
    setShouldAutoScroll(isAtBottom);
  }, [isAtBottom]);

  // Handle manual scrolling
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // If user scrolls up away from bottom, disable auto-scroll
      if (distanceFromBottom > threshold) {
        setShouldAutoScroll(false);
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  // Scroll to bottom function (memoized to prevent effects depending on it from re-running).
  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current || !enabled) return;

    const scrollElement = scrollRef.current;

    if (smooth) {
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth',
      });
    } else {
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }

    setShouldAutoScroll(true);
  }, [enabled, smooth]);

  // Auto-scroll on content change
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !enabled || !shouldAutoScroll || !autoScrollOnContentChange) return;

    const currentScrollHeight = scrollElement.scrollHeight;

    // Only scroll if content height actually changed (new messages)
    if (currentScrollHeight !== lastScrollHeight.current) {
      lastScrollHeight.current = currentScrollHeight;

      // Use RAF for smoother scrolling
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  });

  return {
    scrollRef,
    bottomRef,
    scrollToBottom,
    isAtBottom: shouldAutoScroll && isAtBottom,
  };
};
